import { Toolbar } from '@box/blueprint-web';
import { Link } from '@box/blueprint-web-assets/icons/Fill';
import { HyperlinkPopover } from '@box/hyperlink-popover';
import { type Editor, getMarkAttributes } from '@tiptap/react';
import React from 'react';
import { useIntl } from 'react-intl';
import messages from '../../../messages';
import { getCompleteURL } from '../../../rte-utils';
import { useHyperlinkPopoverAnchor } from '../hooks/useHyperlinkPopoverAnchor';
import { usePreventScrolling } from '../hooks/usePreventScrolling';

type HyperlinkSettingProps = {
  editor: Editor;
};

const TOGGLE_VALUE = 'hyperlink';

export const HyperlinkSetting = ({ editor }: HyperlinkSettingProps) => {
  const intl = useIntl();

  const [isHyperlinkPopoverOpen, setIsHyperlinkPopoverOpen] =
    React.useState(false);
  const hyperlinkPopoverAnchor = useHyperlinkPopoverAnchor(
    editor,
    isHyperlinkPopoverOpen,
  );

  const markAttr = getMarkAttributes(editor.state, 'link');
  const isLinkActive = !!markAttr?.href;

  usePreventScrolling(isHyperlinkPopoverOpen);

  const closePopover = () => {
    setIsHyperlinkPopoverOpen(false);
    editor.commands.focus();
  };

  const handleClear = () => {
    editor.chain().focus().unsetLink().run();
    closePopover();
  };

  const handleEditChange = (isEditing: boolean) => {
    if (isEditing) {
      editor.commands.setSelectionDecoration();
    } else {
      editor.commands.unsetSelectionDecoration();
      editor.commands.focus();
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setIsHyperlinkPopoverOpen(true);
    } else {
      closePopover();
    }
  };

  const handleSubmit = (submittedUrl: string) => {
    if (!submittedUrl) {
      return;
    }

    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: getCompleteURL(submittedUrl) })
      .run();

    editor.commands.unsetSelectionDecoration();
    editor.commands.focus();
    closePopover();
  };

  return (
    <>
      {isHyperlinkPopoverOpen && (
        <HyperlinkPopover
          anchor={hyperlinkPopoverAnchor}
          data-testid="hyperlink-popover"
          defaultIsEditing={!isLinkActive}
          defaultIsOpen
          defaultUrl={getMarkAttributes(editor.state, 'link')?.href || ''}
          onClear={handleClear}
          onEditChange={handleEditChange}
          onOpenChange={handleOpenChange}
          onSubmit={handleSubmit}
          // zIndex must be set otherwise it will appear behind the form
          zIndex={50}
        />
      )}
      <Toolbar.ToggleGroup
        type="single"
        value={isLinkActive ? TOGGLE_VALUE : ''}
      >
        <Toolbar.ToggleItem
          aria-label={intl.formatMessage(
            messages.editorToolbarHyperlinkAriaLabel,
          )}
          icon={Link}
          onClick={() => handleOpenChange(!isHyperlinkPopoverOpen)}
          value={TOGGLE_VALUE}
          data-testid="toolbar-item-hyperlink"
        />
      </Toolbar.ToggleGroup>
    </>
  );
};
